import { Button, CloseButton, Dialog, Portal } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { t } from 'i18next';
import { useState } from 'react';

import { ApiError, CommitmentGoalsService } from '@/client';
import useCustomToast from '@/hooks/use-custom-toast';
import { handleError } from '@/utils';

interface DeleteCommitmentGoalModalProps {
  commitmentId: string;
  commitmentGoalId: string;
  commitmentGoalName: string;
  onDelete?: (id: string) => void;
  onCancel?: () => void;
  children: React.ReactNode;
}

export const CommitmentGoalDeleteDialog = ({
  commitmentId,
  commitmentGoalId,
  commitmentGoalName,
  onDelete,
  onCancel,
  children,
}: DeleteCommitmentGoalModalProps) => {
  const { slug } = useParams({ strict: false });
  const queryClient = useQueryClient();
  const showToast = useCustomToast();
  const [open, setOpen] = useState(false);

  const mutationCommitmentGoal = useMutation({
    mutationFn: async () =>
      await CommitmentGoalsService.deleteGoal({
        companySlug: slug,
        id: commitmentGoalId,
      }),
    onSuccess: async () => {
      showToast('Succès', 'Objectif supprimer avec succès', 'success');
      await queryClient.invalidateQueries({
        queryKey: ['commitments_goals', { slug, commitment_id: commitmentId }],
      });
      setOpen(false);
      onDelete && onDelete(commitmentGoalId);
    },
    onError: (err: ApiError) => {
      handleError(err, showToast);
    },
  });

  const handleDelete = () => {
    mutationCommitmentGoal.mutate();
  };

  return (
    <Dialog.Root role="alertdialog" lazyMount open={open} onOpenChange={(e) => setOpen(e.open)}>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>

      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Supprimer "{commitmentGoalName}" ?</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <p>
                Cette action est irréversible. L'objectif <strong>{commitmentGoalName}</strong> sera définitivement
                supprimé du plan RSE, ainsi que toutes ses données associées.
              </p>
            </Dialog.Body>

            <Dialog.Footer>
              <Dialog.ActionTrigger asChild onClick={() => onCancel && onCancel()}>
                <Button variant="outline">{t('ui.cancel')}</Button>
              </Dialog.ActionTrigger>

              <Button bg="red.500" onClick={handleDelete}>
                {t('ui.delete')}
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
